import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="ui--reference-data-confirm"
//
// CONEK-1562: Wraps an admin reference data edit form. On submit, fetches the
// live count of FK referencers from the affected_count endpoint. If non-zero,
// opens a confirmation modal warning that the change will retroactively
// rewrite the displayed label of every dependent record. On confirm, lets
// the form submit normally — the backend marks the version as
// `paper_trail_event = "destructive_rename"` whenever the count is positive,
// so no separate confirmation signal is needed in the payload.
//
// Required values:
//   type-value             — e.g. "LeadSource", "OpportunityStage"
//   id-value               — UUID of the record being edited
//   affected-count-url-value — URL of admin/reference_data#affected_count
//
// Required targets:
//   modal       — the <dialog> element rendered with ui--modal controller
//   countDisplay — element whose textContent displays the count
//
// Optional targets:
//   pendingSpinner — element shown while fetching the count
export default class extends Controller {
  static targets = ["modal", "countDisplay", "pendingSpinner"]
  static values = {
    type: String,
    id: String,
    affectedCountUrl: String,
  }

  connect() {
    this._submitHandler = this._handleSubmit.bind(this)
    this.element.addEventListener("submit", this._submitHandler)
  }

  disconnect() {
    this.element.removeEventListener("submit", this._submitHandler)
  }

  // --- Form submit interception ---

  async _handleSubmit(event) {
    if (this._confirmed) {
      // Second pass — let the form submit normally
      return
    }
    event.preventDefault()

    if (this._pending) {
      // Already fetching the count from a prior submit — debounce the duplicate.
      // The original request will open the modal when it resolves.
      return
    }

    if (!this.idValue) {
      // No id means new record — nothing to count, submit straight through
      this._confirmAndSubmit()
      return
    }

    this._pending = true
    this._setSubmitButtonsDisabled(true)
    this._showPendingSpinner()
    let count = 0
    try {
      const url = new URL(this.affectedCountUrlValue, window.location.origin)
      url.searchParams.set("type", this.typeValue)
      url.searchParams.set("id", this.idValue)

      const response = await get(url.toString(), {
        credentials: "include",
        responseKind: "json",
      })
      if (response.ok) {
        const data = await response.json
        count = Number.isFinite(data?.count) ? data.count : 0
      }
    } catch {
      // On network error fall through to a non-zero default so the user is
      // still prompted before silently rewriting history.
      count = -1
    } finally {
      this._pending = false
      this._hidePendingSpinner()
    }

    if (count === 0) {
      // No dependents — no risk of retroactive rewrite. Submit straight through.
      // Submit buttons stay disabled (form is navigating away).
      this._confirmAndSubmit()
      return
    }

    if (this.hasCountDisplayTarget) {
      this.countDisplayTarget.textContent = count < 0 ? "?" : String(count)
    }

    this._openModal()
    // Submit buttons stay disabled while the modal is open. Re-enabled on
    // cancel(). On confirm() the form submits and the page navigates away.
  }

  // --- Modal action handlers (data-action wired in the view) ---

  cancel() {
    this._closeModal()
    this._setSubmitButtonsDisabled(false)
  }

  confirm() {
    this._closeModal()
    this._confirmAndSubmit()
  }

  // --- Internal helpers ---

  _confirmAndSubmit() {
    this._confirmed = true
    this.element.requestSubmit()
  }

  _setSubmitButtonsDisabled(disabled) {
    const buttons = this.element.querySelectorAll(
      "button[type='submit'], input[type='submit']",
    )
    buttons.forEach((b) => {
      b.disabled = disabled
      b.setAttribute("aria-busy", disabled ? "true" : "false")
    })
  }

  _openModal() {
    if (!this.hasModalTarget) return
    const controller = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
    if (controller) {
      controller.open()
    } else if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    }
  }

  _closeModal() {
    if (!this.hasModalTarget) return
    const controller = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
    if (controller) {
      controller.close()
    } else if (this.modalTarget.open) {
      this.modalTarget.close()
    }
  }

  _showPendingSpinner() {
    if (this.hasPendingSpinnerTarget) {
      this.pendingSpinnerTarget.classList.remove("hidden")
    }
  }

  _hidePendingSpinner() {
    if (this.hasPendingSpinnerTarget) {
      this.pendingSpinnerTarget.classList.add("hidden")
    }
  }
}
